import {
  AlreadyPrrocessIcon,
  CanceledIcon,
  ConsolidateUnprocessedIcon,
  DoDisturbAltIcon,
  ModernTableColumn,
  NotFoundIcon,
  PackageCheckIcon,
  ScalesIcon,
  SuccessLineIcon,
  TriangleAlertIcon,
  CloseIcon,
} from '@uni-space/uni-components';
import { useMemo } from 'react';
import { BigsSuccessRow, RecommendedBoxEnum } from './bigs';
import { useTranslation } from 'react-i18next';
import { StatusAcceptanceEnum } from '@features/ConsolidatorTracking/model/consolidator';

const recommendedBoxLabelKeys: Record<RecommendedBoxEnum, string | null> = {
  [RecommendedBoxEnum.BoxUnspecified]: null,
  [RecommendedBoxEnum.BoxL]: 'recommendedBox.boxL',
  [RecommendedBoxEnum.Box80]: 'recommendedBox.box80',
  [RecommendedBoxEnum.Box180]: 'recommendedBox.box180',
  [RecommendedBoxEnum.Bag]: 'recommendedBox.bag',
};

export function useBigsTableSettings() {
  const { t } = useTranslation();

  const getErrorStatusView = (status: StatusAcceptanceEnum) => {
    switch (status) {
      case StatusAcceptanceEnum.Already:
        return {
          label: t('alreadyScanned'),
          component: <AlreadyPrrocessIcon color={'#f57c00'} size="s" />,
        };
      case StatusAcceptanceEnum.AlreadyAnotherWarehouse:
        return {
          label: t('alreadyAnotherWarehouse'),
          component: <DoDisturbAltIcon color={'#f57c00'} size="s" />,
        };
      case StatusAcceptanceEnum.Canceled:
        return {
          label: t('canceled'),
          component: <CanceledIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.ServiceRestriction:
        return {
          label: t('serviceRestriction'),
          component: <ConsolidateUnprocessedIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.NotFound:
        return {
          label: t('parcelNotFound'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.UserNotFound:
        return {
          label: t('userNotFound'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.QualityControl:
        return {
          label: t('qualityControl'),
          component: <PackageCheckIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.HighDensity:
        return {
          label: t('highDensity'),
          component: <ScalesIcon color="#EF6D7C" size="s" />,
        };
      case StatusAcceptanceEnum.InvalidDimensions:
        return {
          label: t('invalidDimensions'),
          component: <TriangleAlertIcon color="#EF6D7C" size="s" />,
        };
      default:
        return {
          label: t('parcelNotFound'),
          component: <NotFoundIcon color="#EF6D7C" size="s" />,
        };
    }
  };

  const successColumns: ModernTableColumn<BigsSuccessRow>[] = useMemo(
    () => [
      {
        key: 'trackNumber',
        label: t('tableHeader.trackNumber'),
        render: (row) => <span className="ct6-code">{row.trackNumber}</span>,
        sort: true,
        width: 160,
        align: 'center',
      },
      {
        key: 'regionZone',
        label: t('tableHeader.regionZone'),
        render: (row) => row.regionZone,
        align: 'center',
        width: 90,
      },
      { key: 'bagCode', label: t('bagCode'), render: (row) => row.bagCode, align: 'center', width: 120 },
      { key: 'sortingCode', label: t('sortingCode'), render: (row) => row.sortingCode, align: 'center', width: 130 },
      {
        key: 'weight',
        label: t('tableHeader.weight'),
        render: (row) => (row.weight == null ? '—' : row.weight),
        sort: true,
        sortValue: (row) => row.weight ?? -1,
        align: 'center',
        width: 90,
      },
      {
        key: 'length',
        label: t('tableHeader.length'),
        render: (row) => (row.length == null ? '—' : row.length),
        align: 'center',
        width: 90,
      },
      {
        key: 'width',
        label: t('tableHeader.width'),
        render: (row) => (row.width == null ? '—' : row.width),
        align: 'center',
        width: 90,
      },
      {
        key: 'height',
        label: t('tableHeader.height'),
        render: (row) => (row.height == null ? '—' : row.height),
        align: 'center',
        width: 90,
      },
      {
        key: 'density',
        label: t('tableHeader.density'),
        render: (row) => (row.density == null ? '—' : row.density.toFixed(2)),
        sort: true,
        sortValue: (row) => row.density ?? -1,
        align: 'center',
        width: 100,
      },
      {
        key: 'recommendedBox',
        label: t('tableHeader.recommendedBox'),
        render: (row) => {
          const labelKey = row.recommendedBox == null ? null : recommendedBoxLabelKeys[row.recommendedBox];
          if (!labelKey) return '—';
          return <span className={`bigs-box bigs-box--${row.recommendedBox}`}>{t(labelKey)}</span>;
        },
        sort: true,
        align: 'center',
        width: 170,
      },
      {
        key: 'needGroundTransport',
        label: t('tableHeader.groundTransport'),
        render: (row) =>
          row.needGroundTransport ? <SuccessLineIcon size="xs" color="green" /> : <CloseIcon color="red" size="xs" />,
        align: 'center',
        width: 150,
      },
    ],
    [t],
  );

  return { successColumns, getErrorStatusView };
}
